import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';

import { conAlgunSegmento } from '../features/home-v2/utils/route-matchers.util';
import { NoSessionAuthGuard } from '../shared/guards/no-session-auth.guard';
import { maintenanceCanActivateChild, maintenanceGuard } from '../shared/guards/maintenance.guard';
import { canActivateChild } from '../shared/guards/permission-user.guard';
import { SessionAuthGuard } from '../shared/guards/session-auth.guard';
import { AppLayoutComponent } from './components/app-layout/app-layout.component';

export const routes: Routes = [
  {
    path: 'auth',
    loadChildren: () => import('../features/app-auth/app-auth.module').then((m) => m.AppAuthModule),
    canActivate: [NoSessionAuthGuard],
  },
  {
    path: 'home',
    component: AppLayoutComponent,
    // Mantenimiento: los super admins pasan; un cajero con una venta a medio armar
    // también (ver `maintenance-flow.util.ts`). En los hijos para cubrir cada navegación.
    canActivate: [SessionAuthGuard, maintenanceGuard],
    canActivateChild: [maintenanceCanActivateChild],
    children: [
      /*
       * Estas cuatro ramas NO llevan guard de permiso en el padre, a propósito.
       *
       * Lo llevaban, y con UN solo permiso cada una: `settings` exigía
       * `VER_PERFIL_PERMISO_PRE`, `experiences` `VER_INFORMACION_EXP`, `venues`
       * `VER_INFORMACION_VEN`, `users` `VER_USUARIO_US`. Como el guard del padre corre
       * antes que el del hijo, ese permiso era peaje obligatorio para TODA la rama,
       * aunque el hijo pidiera otro.
       *
       * Eso rebotaba a perfiles con permisos legítimos. El caso que lo destapó: un
       * perfil con todo el módulo Configuración (`VER_ATRIBUTOS_CONF`,
       * `VER_TERMINOS_CONF`, `VER_CATEGORIA_EVENTO_CONF`,
       * `VER_CAMPOS_ADICIONALES_CONF` — módulo 37) pero sin los de Perfiles (módulo 2)
       * veía los ítems en el menú —que filtra por el `access` de cada subítem— y al
       * entrar caía en `/home`, o sea «Mi cuenta». Los dos únicos ítems del acordeón
       * que le funcionaban eran Cuentas Asíncronas y Banners de cartelera, que apuntan
       * a `./admin/…` y no pasan por acá. Mismo cuadro con «Crear usuario» sin
       * `VER_USUARIO_US`, o con las listas de experiencias teniendo solo
       * `VER_MENU_*_EXP`.
       *
       * El permiso real de cada pantalla lo declara su ruta hoja, que es donde
       * corresponde: están cubiertas todas (se agregaron las dos que faltaban,
       * `settings/white-label` y `experiences/:id/view`). La visibilidad del menú la
       * gobiernan aparte los `MENU_*_MN`.
       */
      {
        path: 'experiences',
        loadChildren: () => import('../features/experiences/experiences.module').then((m) => m.ExperiencesModule),
      },
      {
        path: 'venues',
        loadChildren: () => import('../features/venues/venues.module').then((m) => m.VenuesModule),
      },
      {
        path: 'users',
        loadChildren: () => import('../features/users/users.module').then((m) => m.UsersModule),
      },
      {
        path: 'settings',
        loadChildren: () => import('../features/settings/settings.module').then((m) => m.SettingsModule),
      },
      {
        path: 'admin',
        loadChildren: () => import('../features/admin/admin.module').then((m) => m.AdminModule),
      },
      {
        path: 'account',
        loadChildren: () => import('../features/account/account.module').then((m) => m.AccountModule),
      },
      {
        path: 'producer',
        canActivate: [canActivateChild],
        data: { requiredPermission: 'CREAR_INFORMACION_EXP' },
        loadComponent: () => import('../features/producer/producer.component').then((c) => c.ProducerComponent),
      },
      // DASHBOARD DESMONTADO A PROPÓSITO (2026-09-08). El feature sigue en
      // `features/dashboard` (rutas en `dashboard.routes.ts`). Su backend sirve datos
      // solo para los dominios que `dashboard_control.domain_availability` marca
      // disponibles, y hoy los siete (TICKETS, FINANCE, INVENTORY, GOALS, ATTRIBUTION,
      // AUDIENCE, CHECKINS) están en UNAVAILABLE en dev y demo —la reconciliación
      // nunca pasó, `last_pass_at` es NULL—, así que la pantalla quedaba vacía. Mientras
      // la reconciliación no marque dominios disponibles, `/home/dashboard` cae en el
      // comodín de abajo y va a «Mi perfil», que es a donde iba la raíz antes de que
      // existiera el dashboard. Para volver a montarlo:
      //
      //   { path: 'dashboard', canActivate: [canActivateChild],
      //     data: { requiredPermission: 'VER_INFORMACION_EXP' },
      //     loadChildren: () => import('../features/dashboard/dashboard.routes').then((m) => m.DASHBOARD_ROUTES) }
      {
        path: '**',
        pathMatch: 'full',
        redirectTo: 'account',
      },
    ],
  },
  {
    path: 'sale-validate',
    loadChildren: () => import('../features/sale-validate/sale-validate.module').then((m) => m.SaleValidationModule),
  },
  {
    // Ruta PÚBLICA (sin guard): vista/descarga del PDF del boleto por `code`,
    // enlazada desde el correo del boleto. Reemplaza la /ticket/<code> del sistema viejo.
    path: 'ticket',
    loadChildren: () => import('../features/ticket/ticket.module').then((m) => m.TicketModule),
  },
  {
    // El flujo de compra rediseñado vivió bajo `/home-v2/…` mientras se construía;
    // desde el 2026-09-09 está montado en la raíz (ver abajo). Este redirect mantiene
    // vivos los enlaces y marcadores viejos: `/home-v2/mi-evento/step2?x=1` →
    // `/mi-evento/step2?x=1`, con query y fragmento.
    path: 'home-v2',
    children: [
      {
        path: '**',
        redirectTo: ({ url, queryParams, fragment }) =>
          inject(Router).createUrlTree(['/', ...url.map((s) => s.path)], {
            queryParams,
            fragment: fragment ?? undefined,
          }),
      },
    ],
  },
  {
    // Pantalla pública de «estamos en mantenimiento». Sin guard (es adonde manda el
    // guard) y antes del matcher de eventos, que si no la tomaría como slug.
    path: 'mantenimiento',
    title: 'Estamos en mantenimiento',
    loadComponent: () =>
      import('../features/maintenance/maintenance-page.component').then((c) => c.MaintenancePageComponent),
  },
  {
    // Prototipo de rediseño mobile del home (maquetado estático, datos dummy).
    // Ruta aislada — no toca el home real. Debe ir ANTES de `path: ''` porque el
    // path vacío hace prefix-match de cualquier URL.
    path: 'home-v3',
    canActivate: [maintenanceGuard],
    loadComponent: () => import('../features/home-v3/home-v3.component').then((c) => c.HomeV3Component),
  },
  {
    path: 'privacy/preferences',
    loadComponent: () =>
      import('../features/privacy/privacy-preferences.component').then(
        (component) => component.PrivacyPreferencesComponent
      ),
  },
  {
    // Ruta PÚBLICA (sin guard): términos y condiciones, enlazada desde el
    // footer de todos los correos (URL_BASE_GOLIIIVE + 'terminos-condiciones').
    // Debe ir ANTES de `path: ''` porque el path vacío hace prefix-match y la
    // trataría como slug de evento (aterrizaba en el flujo de steps).
    path: 'terminos-condiciones',
    title: 'Términos y condiciones',
    loadComponent: () =>
      import('../features/legal/terminos-condiciones-page.component').then(
        (component) => component.TerminosCondicionesPageComponent
      ),
  },
  {
    // FLUJO DE COMPRA PRINCIPAL (rediseño, antes `/home-v2`): `/:slug/step1 … step6`,
    // `/mis-boletos/:code` y `/thank-you/complete`. Va ANTES del `HomeModule` original
    // y sin comodín propio: lo que no coincide acá —`venue/:slug`, `p/:token`,
    // `event-not-found`— sigue a la ruta siguiente. Los pasos del flujo original
    // quedan sin alcanzar (ocultos, no borrados): estas rutas los atrapan primero.
    //
    // `matcher` y no `path: ''`: con path vacío la URL `/` quedaría acá (Angular da
    // por coincidente la ruta aunque ningún hijo coincida con lo que sobra, que es
    // nada, y no retrocede) y la cartelera no se vería nunca. El matcher exige al
    // menos un segmento, así `/` sigue siendo del flujo original.
    matcher: conAlgunSegmento,
    // Mantenimiento: corta al ENTRAR al evento (paso 1). Quien ya tiene el temporizador de
    // compra corriendo, o vuelve de la pasarela, sigue (ver `maintenance-flow.util.ts`).
    canActivate: [maintenanceGuard],
    canActivateChild: [maintenanceCanActivateChild],
    loadChildren: () => import('../features/home-v2/home-v2.routes').then((m) => m.HOME_V2_ROUTES),
  },
  {
    // Flujo original. Hoy solo sirve lo que el rediseño no reemplaza (ver arriba).
    path: '',
    canActivate: [maintenanceGuard],
    canActivateChild: [maintenanceCanActivateChild],
    loadChildren: () => import('../features/home/home.module').then((m) => m.HomeModule),
  },
  {
    path: '**',
    pathMatch: 'full',
    redirectTo: '',
  },
];
